/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { FormattedMessage } from '../../../../util/reactIntl';
import { LISTING_STATE_PENDING_APPROVAL, propTypes } from '../../../../util/types';
import { ensureCurrentUser } from '../../../../util/data';

import {
  AvatarLarge,
  InlineTextButton,
  NamedLink,
  NotificationBadge,
} from '../../../../components';

import css from './TopbarMobileMenu.module.css';
import { USER_ROLE } from '../../../../constants/user';
import { LISTING_PAGE_PENDING_APPROVAL_VARIANT, createSlug } from '../../../../util/urlHelpers';
import { SUPPORTED_TABS } from '../../../../constants/listing';

const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    currentUser,
    notificationCount,
    onLogout,
    currentUserRole,
    currentUserListingParams,
    currentUserHasAnyListings,
    currentUserHasListings,
  } = props;

  const user = ensureCurrentUser(currentUser);

  if (!isAuthenticated) {
    const signup = (
      <NamedLink name="SignupPage" className={css.signupLink}>
        <FormattedMessage id="TopbarMobileMenu.signupCustomerLink" />
      </NamedLink>
    );

    const signupAsArtist = (
      <NamedLink name="ArtistSignupPage" className={css.signupLink}>
        <FormattedMessage id="TopbarMobileMenu.signupArtistLink" />
      </NamedLink>
    );

    const login = (
      <NamedLink name="LoginPage" className={css.loginLink}>
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </NamedLink>
    );

    const signupOrLogin = (
      <span className={css.authenticationLinks}>
        <FormattedMessage
          id="TopbarMobileMenu.signupLogin"
          values={{ signup, signupAsArtist, login, lineBreak: <br /> }}
        />
      </span>
    );
    return (
      <div className={css.root}>
        <div className={css.content}>
          <div className={css.authenticationGreeting}>
            <FormattedMessage
              id="TopbarMobileMenu.unauthorizedGreeting"
              values={{ lineBreak: <br />, signupOrLogin }}
            />
          </div>
        </div>
        <div className={css.footer}>
          <NamedLink className={css.createNewListingLink} name="NewListingPage">
            <FormattedMessage id="TopbarMobileMenu.newListingLink" />
          </NamedLink>
        </div>
      </div>
    );
  }

  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;

  const displayName = user.attributes.profile.firstName;
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  const createProfileLink = !currentUserHasAnyListings && (
    <NamedLink className={css.artistProfileLink} name="NewListingPage">
      <FormattedMessage id="General.createProfile" />
    </NamedLink>
  );

  const { id, title, variant } = currentUserListingParams || {};
  const lastTab = SUPPORTED_TABS[SUPPORTED_TABS.length - 1];

  const finishProfileLink = currentUserHasAnyListings && !currentUserHasListings && (
    <NamedLink
      className={css.artistProfileLink}
      name="EditListingPage"
      params={{ id, slug: createSlug(title), type: variant, tab: lastTab }}
    >
      <FormattedMessage id="General.finishProfile" />
    </NamedLink>
  );

  const viewProfileLink = currentUserHasListings && (
    <NamedLink
      className={css.artistProfileLink}
      name={variant === LISTING_STATE_PENDING_APPROVAL ? 'ListingPageVariant' : 'ListingPage'}
      params={{
        id,
        slug: createSlug(title),
        ...(variant === LISTING_STATE_PENDING_APPROVAL && {
          variant: LISTING_PAGE_PENDING_APPROVAL_VARIANT,
        }),
      }}
    >
      <FormattedMessage id="General.viewProfile" />
    </NamedLink>
  );

  const artistProfileLink =
    currentUserRole === USER_ROLE.ARTIST &&
    (createProfileLink || finishProfileLink || viewProfileLink);

  return (
    <div className={css.root}>
      <AvatarLarge className={css.avatar} user={currentUser} />
      <div className={css.content}>
        <span className={css.greeting}>
          <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
        </span>
        <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
          <FormattedMessage id="TopbarMobileMenu.logoutLink" />
        </InlineTextButton>
        <NamedLink
          className={classNames(css.inbox, currentPageClass('ManageBookingsPage'))}
          name="ManageBookingsPage"
        >
          <FormattedMessage id="General.yourBookings" />
          {notificationCountBadge}
        </NamedLink>

        <NamedLink
          className={classNames(css.inbox, currentPageClass('EnquiriesPage'))}
          name="EnquiriesPage"
        >
          <FormattedMessage id="General.yourEnquiries" />
        </NamedLink>

        {currentUserRole === USER_ROLE.USER && (
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
          </NamedLink>
        )}
        <NamedLink
          className={classNames(css.navigationLink, currentPageClass('AccountSettingsPage'))}
          name="AccountSettingsPage"
        >
          <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
        </NamedLink>
        <div className={css.spacer} />
      </div>
      {artistProfileLink}
    </div>
  );
};

TopbarMobileMenu.defaultProps = { currentUser: null, notificationCount: 0, currentPage: null };

const { bool, func, number, string } = PropTypes;

TopbarMobileMenu.propTypes = {
  isAuthenticated: bool.isRequired,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  notificationCount: number,
  onLogout: func.isRequired,
  currentUserRole: string,
};

export default TopbarMobileMenu;
